// src/modules/student-exit/components/StudentExitStudentTable.tsx
import Datatable from "components/Common/dataTable";
import { useTranslation } from "react-i18next";
import React from "react";
import { formatToLocaleDate } from "utils/dateUtils";
import ActionsDropdown, { Action } from "components/Common/ActionsDropdown";
import { showConfirmDialog } from "utils/confirmDialogUtils";
import StudentExitStatusBadge from "components/Common/Badge/StudentExitStatusBadge";
import CustomAlert from "components/Common/ui/CustomAlert";
import ExitTypeBadge from "components/Common/Badge/ExitTypeBadge";

type Props = {
    dataSource: any[];
    loading?: boolean;
    onDelete?: (record: any) => void;
    dataPaginate: any;
    onChange: (pg: any) => void;
};

const StudentExitStudentTable: React.FC<Props> = ({
    dataSource,
    loading,
    onDelete,
    dataPaginate,
    onChange,
}) => {
    const { t } = useTranslation();

    const handleDelete = (record: any) => {
        showConfirmDialog({
            title: t("studentExit.confirmDelete.title"),
            content: t("studentExit.confirmDelete.message"),

            onOk: () => onDelete?.(record.id),
        });
    };

    const columns = [
        {
            title: t("studentExit.form.pickupPerson"),
            key: "pickupPerson",
            render: (_: any, record: any) => (
                <span>
                    {record.authorizedBy ?
                        `${record.authorizedBy.fullName} (${t('studentExit.pickupType.authorized')})` :
                        record.guardian ?
                            `${record.guardian.user.firstName} ${record.guardian.user.lastName} (${t('studentExit.pickupType.guardian')})` :
                            '—'
                    }
                </span>
            ),
        },
        {
            title: t("studentExit.form.exitType"),
            dataIndex: "exitType",
            key: "exitType",
            render: (exitType: string) => (
                <ExitTypeBadge exitType={exitType as any} />
            ),
        },
        {
            title: t("studentExit.form.exitTime"),
            dataIndex: "exitTime",
            key: "exitTime",
            render: (v: string) => formatToLocaleDate(v, 'L LT') ?? "—",
        },
        {
            title: t("studentExit.form.status"),
            dataIndex: "status",
            key: "status",
            align: "center" as const,
            render: (status: string) => (
                <StudentExitStatusBadge status={status as any} />
            ),
        },
        {
            title: t("studentExit.form.completedAt"),
            dataIndex: "completedAt",
            key: "completedAt",
            render: (v: string) => formatToLocaleDate(v, 'L LT') ?? "—",
        },
        {
            title: t("created_at"),
            dataIndex: "createdAt",
            key: "createdAt",
            render: (v: string) => formatToLocaleDate(v) ?? "—",
        },
        {
            title: t("actions"),
            key: "actions",
            render: (_: string, record: any) => {
                const actions: Action[] = [];

                // Only show delete for pending or rejected status
                if ((record.status === 'pending') && onDelete) {
                    actions.push({
                        label: t("delete"),
                        icon: "ti ti-trash",
                        actionType: "link",
                        onClick: () => handleDelete(record),
                    });
                }

                return actions.length > 0 ? (
                    <ActionsDropdown actions={actions} />
                ) : (
                    <span className="text-muted">
                        <i className="ti ti-lock"></i>
                    </span>
                );
            },
        },
    ];

    return (
        <div className="table-responsive">
            <Datatable
                rowKey="id"
                columns={columns}
                dataSource={dataSource}
                loading={loading}
                pagination={true}
                dataPaginate={dataPaginate}
                onChange={onChange}
                expandable={{
                    expandedRowRender: (record: any) => record.notes ? (
                        <CustomAlert
                            type="info"
                            showIcon
                            message={t("studentExit.form.notes")}
                            description={record.notes}
                        />
                    ) : null,
                    rowExpandable: (record: any) => !!record.notes,
                    expandedRowClassName: () => "bg-transparent",
                }}
            />
        </div>
    );
};

export default StudentExitStudentTable;